import Slide from '@/components/shared/Slide';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { GrantProgram, GoNoGo, ViewMode } from '@/types/roadmap.types';

interface Props {
  programs: GrantProgram[];
  mode: ViewMode;
}

const DOT_COLORS: Record<string, string> = {
  primary: 'bg-indigo-500',
  go: 'bg-indigo-500',
  conditional: 'bg-amber-500',
  monitor: 'bg-slate-400',
};

const BADGE_STYLES: Record<string, string> = {
  primary: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  go: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  conditional: 'bg-amber-50 text-amber-700 border-amber-200',
  monitor: 'bg-blue-50 text-blue-700 border-blue-200',
};

const BADGE_LABELS: Record<string, string> = {
  primary: 'PRIMARY',
  go: 'GO',
  conditional: 'PREPARE',
  monitor: 'MONITOR',
};

const STATUS_STYLES: Record<string, string> = {
  specific: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  rolling: 'bg-blue-50 text-blue-600 border-blue-200',
  expected: 'bg-amber-50 text-amber-600 border-amber-200',
  monthly: 'bg-violet-50 text-violet-600 border-violet-200',
};

const STATUS_LABELS: Record<string, string> = {
  specific: 'OPEN',
  rolling: 'ROLLING',
  expected: 'EXPECTED',
  monthly: 'MONTHLY',
};

const DAY_HEADERS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

interface CalendarDay {
  day: number;
  programs: GrantProgram[];
}

function buildCalendarWeeks(
  year: number,
  month: number,
  programsByDate: Map<string, GrantProgram[]>,
): CalendarDay[][] {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const weeks: CalendarDay[][] = [];
  let week: CalendarDay[] = [];

  for (let i = 0; i < firstDay; i++) {
    week.push({ day: 0, programs: [] });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    week.push({ day: d, programs: programsByDate.get(key) ?? [] });
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push({ day: 0, programs: [] });
    weeks.push(week);
  }
  return weeks;
}

function getGoNoGoPriority(g: GoNoGo): number {
  const order: Record<string, number> = { primary: 0, go: 1, conditional: 2, monitor: 3, no_go: 4 };
  return order[g] ?? 4;
}

function getDotColor(programs: GrantProgram[]): string {
  const best = programs.reduce(
    (acc, p) => (getGoNoGoPriority(p.go_no_go) < getGoNoGoPriority(acc) ? p.go_no_go : acc),
    'no_go' as GoNoGo,
  );
  return DOT_COLORS[best] ?? 'bg-slate-400';
}

interface MonthGridProps {
  year: number;
  month: number;
  programsByDate: Map<string, GrantProgram[]>;
  today: Date;
}

function MonthGrid({ year, month, programsByDate, today }: MonthGridProps) {
  const weeks = buildCalendarWeeks(year, month, programsByDate);
  const monthName = new Date(year, month).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <Card className="shadow-sm print:shadow-none print:break-inside-avoid">
      <CardHeader className="pb-2 px-4 pt-4 print:p-2 print:pb-1">
        <CardTitle className="text-sm font-bold text-foreground print:text-[9pt]">{monthName}</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 print:p-2 print:pt-0">
        <table className="w-full border-collapse text-center">
          <thead>
            <tr>
              {DAY_HEADERS.map((d) => (
                <th key={d} className="text-[10px] font-medium text-muted-foreground py-1.5 print:text-[6pt] print:py-0.5">
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, wi) => (
              <tr key={wi}>
                {week.map((cell, ci) => {
                  if (cell.day === 0) {
                    return <td key={ci} className="h-9 print:h-5" />;
                  }
                  const cellKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`;
                  const isToday = cellKey === todayKey;
                  const hasPrograms = cell.programs.length > 0;

                  return (
                    <td key={ci} className="h-9 print:h-5 relative">
                      <span
                        className={cn(
                          'inline-flex items-center justify-center w-7 h-7 text-xs rounded-full print:w-4 print:h-4 print:text-[7pt]',
                          isToday && 'bg-foreground text-background font-bold',
                          !isToday && 'text-foreground/80',
                        )}
                      >
                        {cell.day}
                      </span>
                      {hasPrograms && (
                        <span
                          className={cn(
                            'absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full print:w-1 print:h-1',
                            getDotColor(cell.programs),
                          )}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

const PRINT_MAX_FIRST_PAGE = 8;
const PRINT_MAX_CONTINUATION = 20;

function DeadlineTableBlock({
  programs,
  isOverflow,
}: {
  programs: GrantProgram[];
  isOverflow: boolean;
}) {
  return (
    <Card className={cn('shadow-sm print:shadow-none', isOverflow && 'print:break-before-page')}>
      <CardHeader className="pb-0 px-4 pt-3 print:p-2 print:pb-0">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground print:text-[7pt]">
          {isOverflow ? 'Upcoming Deadlines (cont.)' : 'Upcoming Deadlines'}
        </p>
      </CardHeader>
      <CardContent className="px-0 pb-0 print:p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[600px]">
            <thead>
              <tr className="border-b border-border/40">
                <th className="px-4 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider print:text-[6pt] print:px-2 print:py-1">Program</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider print:text-[6pt] print:px-2 print:py-1">Grantor</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider print:text-[6pt] print:px-2 print:py-1">Deadline</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider print:text-[6pt] print:px-2 print:py-1">Status</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider text-right print:text-[6pt] print:px-2 print:py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((p) => {
                const d = p.deadline?.date ? new Date(p.deadline.date + 'T00:00:00') : null;
                const formatted = d
                  ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '';
                const dlType = p.deadline?.type ?? 'expected';

                return (
                  <tr key={p.id} className="border-b border-border/20 last:border-0">
                    <td className="px-4 py-2 print:px-2 print:py-1">
                      <div className="flex items-center gap-2">
                        <span className={cn('w-2 h-2 rounded-full shrink-0 print:w-1.5 print:h-1.5', DOT_COLORS[p.go_no_go] ?? 'bg-slate-400')} />
                        <span className="text-xs font-medium text-foreground truncate max-w-[260px] print:text-[7pt] print:max-w-[180px]">
                          {p.program_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-[11px] text-muted-foreground print:px-2 print:py-1 print:text-[7pt]">
                      {p.grantor}
                    </td>
                    <td className="px-4 py-2 print:px-2 print:py-1">
                      <span className="text-xs font-medium text-foreground print:text-[7pt]">{formatted}</span>
                    </td>
                    <td className="px-4 py-2 print:px-2 print:py-1">
                      <span className={cn('inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide print:text-[6pt]', STATUS_STYLES[dlType] ?? STATUS_STYLES.expected)}>
                        {STATUS_LABELS[dlType] ?? 'EXPECTED'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right print:px-2 print:py-1">
                      <span className={cn('inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide print:text-[6pt]', BADGE_STYLES[p.go_no_go] ?? BADGE_STYLES.monitor)}>
                        {BADGE_LABELS[p.go_no_go] ?? 'MONITOR'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {programs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-xs text-muted-foreground italic print:text-[7pt]">
                    No deadlines in the next 60 days
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function DeadlineTable({ programs }: { programs: GrantProgram[] }) {
  if (programs.length <= PRINT_MAX_FIRST_PAGE) {
    return <DeadlineTableBlock programs={programs} isOverflow={false} />;
  }

  const pages: GrantProgram[][] = [programs.slice(0, PRINT_MAX_FIRST_PAGE)];
  for (let i = PRINT_MAX_FIRST_PAGE; i < programs.length; i += PRINT_MAX_CONTINUATION) {
    pages.push(programs.slice(i, i + PRINT_MAX_CONTINUATION));
  }

  return (
    <>
      {pages.map((batch, i) => (
        <DeadlineTableBlock key={i} programs={batch} isOverflow={i > 0} />
      ))}
    </>
  );
}

export default function DeadlineCalendarSlide({ programs, mode }: Props) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

  const windowEnd = new Date(nextYear, nextMonth + 1, 0);

  const visible = programs.filter((p) => p.go_no_go !== 'no_go');

  const programsByDate = new Map<string, GrantProgram[]>();
  const calendarPrograms: GrantProgram[] = [];
  const futureRecurring: GrantProgram[] = [];

  for (const p of visible) {
    const dateStr = p.deadline?.date;
    const dlType = p.deadline?.type;

    if (dlType === 'rolling') {
      futureRecurring.push(p);
      if (dateStr) {
        const d = new Date(dateStr + 'T00:00:00');
        if (!isNaN(d.getTime()) && d >= today && d <= windowEnd) {
          const key = dateStr;
          if (!programsByDate.has(key)) programsByDate.set(key, []);
          programsByDate.get(key)!.push(p);
          calendarPrograms.push(p);
        }
      }
      continue;
    }

    if (!dateStr) {
      futureRecurring.push(p);
      continue;
    }

    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) {
      futureRecurring.push(p);
      continue;
    }

    if (d > windowEnd) {
      futureRecurring.push(p);
      continue;
    }

    const key = dateStr;
    if (!programsByDate.has(key)) programsByDate.set(key, []);
    programsByDate.get(key)!.push(p);
    calendarPrograms.push(p);
  }

  calendarPrograms.sort((a, b) => {
    const pa = getGoNoGoPriority(a.go_no_go);
    const pb = getGoNoGoPriority(b.go_no_go);
    if (pa !== pb) return pa - pb;
    return (a.deadline?.date ?? '').localeCompare(b.deadline?.date ?? '');
  });

  futureRecurring.sort((a, b) => getGoNoGoPriority(a.go_no_go) - getGoNoGoPriority(b.go_no_go));

  return (
    <Slide mode={mode} wide>
      <div className="w-full max-w-[1400px] mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2 print:mb-1 print:text-[7pt]">
          Deadline Calendar
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4 print:mb-2 print:text-[16pt]">
          60-Day View
        </h2>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-4 print:mb-2 print:gap-3">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground print:text-[7pt]">
            <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0 print:w-1.5 print:h-1.5" />
            Primary &mdash; Apply now
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground print:text-[7pt]">
            <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 print:w-1.5 print:h-1.5" />
            Prepare &mdash; Needs a partner lead
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground print:text-[7pt]">
            <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0 print:w-1.5 print:h-1.5" />
            Monitor &mdash; No active call
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground print:text-[7pt]">
            <span className="w-2.5 h-2.5 rounded-full bg-foreground shrink-0 print:w-1.5 print:h-1.5" />
            Today ({today.getMonth() + 1}/{today.getDate()})
          </span>
        </div>

        {/* Two-month calendar row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 print:grid-cols-2 print:gap-2 print:mb-3">
          <MonthGrid year={currentYear} month={currentMonth} programsByDate={programsByDate} today={today} />
          <MonthGrid year={nextYear} month={nextMonth} programsByDate={programsByDate} today={today} />
        </div>

        {/* Future / Recurring — full-width band below the calendars */}
        <Card className="mb-4 shadow-sm print:mb-3 print:shadow-none print:break-inside-avoid">
          <CardHeader className="space-y-1 px-4 pb-2 pt-4 print:p-2 print:pb-1">
            <CardTitle className="text-sm font-bold text-foreground print:text-[9pt]">Future / Recurring</CardTitle>
            <p className="text-[11px] leading-snug text-muted-foreground print:text-[6pt]">
              Beyond the 60-day window or no fixed date &mdash; shown compact across columns
            </p>
          </CardHeader>
          <CardContent className="px-4 pb-4 print:p-2 print:pt-0">
            {futureRecurring.length === 0 ? (
              <p className="text-xs italic text-muted-foreground print:text-[7pt]">None</p>
            ) : (
              <div className="columns-1 gap-x-6 sm:columns-2 md:columns-3 [column-fill:balance] print:columns-3 print:gap-x-3">
                {futureRecurring.map((p) => (
                  <div key={p.id} className="mb-3 break-inside-avoid flex items-start gap-2 print:mb-2">
                    <span
                      className={cn(
                        'mt-1.5 h-2 w-2 shrink-0 rounded-full print:mt-1 print:h-1.5 print:w-1.5',
                        DOT_COLORS[p.go_no_go] ?? 'bg-slate-400',
                      )}
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold leading-tight text-foreground print:text-[7pt]">
                        {p.program_name}
                      </p>
                      <p className="text-[10px] leading-snug text-muted-foreground print:text-[6pt]">
                        {p.deadline?.display || (p.deadline?.type === 'rolling' ? 'Rolling' : 'TBD')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Compact deadline list */}
        <DeadlineTable programs={calendarPrograms} />
      </div>
    </Slide>
  );
}
